/**
 * DrawSubModeController — the ENTIRE Draw sub-mode gesture of
 * ConstructionTool, extracted (#850, epic #828). A tool OWNS a
 * controller instance and DRIVES it by composition (it is NOT a base
 * class), mirroring the `SelectEditController` / `VertexEditController`
 * / `RotationController` pattern: the controller exclusively owns the
 * Draw-mode STATE (the drag-out draft, the presets, the live-dims /
 * live-line callbacks, and the shared `DrawGestureEngine` instance),
 * while everything construction-specific it needs from the owning tool
 * (scope / scene / project / settings / commit / selection, the shared
 * `ConstructionSnapController`, and the sibling-gesture reset) is
 * injected through the `DrawSubModeHost` interface.
 *
 * The Draw sub-mode does two things that SHARE draft state:
 *   (a) drag-out parametric shapes (rect / ellipse / slot / line /
 *       freehand — press-drag, sampled to points, NOT run through the
 *       engine); and
 *   (b) the click-to-add polyline / polygon driven by the shared
 *       `DrawGestureEngine`.
 * In `onDown` the fields `startPt` / `currentPt` / `buffer` are seeded
 * for BOTH paths, so they can't be cleanly split — the whole sub-mode
 * moves here as one controller.
 *
 * This controller implements `DrawGestureHost` (the engine calls back
 * into it for every construction-specific concern); the engine-facing
 * hooks (`resolveDraftPlacement` / `resolveDraftHover` / `commitDraft`
 * etc.) forward through the same `DrawSubModeHost` to reach the shared
 * snap machinery + model sinks that stay on ConstructionTool. See
 * docs/draw-tool-architecture.md.
 */
import paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene } from "../canvas/Scene";
import { newId } from "../model/ids";
import { withConstructionLine } from "../model/projectOps";
import {
  isVisible,
  nextConstructionName,
  projectConstructionLines,
  type ConstructionLine,
  type ConstructionPrimitive,
  regularPolygonModifier,
  type Constraint,
  type Project,
} from "../model/Project";
import { isOffsetDerived } from "../model/constructionOffset";
import { isMirrorDerived } from "../model/constructionMirror";
import {
  isPinnableSnapRef,
  snapRefToConstraintRef,
} from "./constructionSnapRefs";
import { smoothPathFromRawPoints } from "../model/freehandSmoothing";
import {
  snapSegmentToLength,
  snapSegmentToTurn,
  turnAt,
} from "../model/regularPolygon";
import { cssVar } from "../colors";
import {
  constrainSquare,
  isAngleSnapActive,
  isGridSnapActive,
  isPointConstraintActive,
  isPointSnapActive,
  resolveDragBox,
  resolveDragAxis,
  snapPointToGrid,
  type AngleSnapResult,
} from "./dragSnap";
import { referenceGhostPoint, type AngleReference } from "./angleReferences";
import { setAngleHint } from "../snapAngleIndicator";
import { emitSegmentRay } from "./drawGesture";
import { roundAllCorners } from "../model/constructionRounding";
import { hasHover } from "../canvas/pointerPrecision";
import { dragToCurveDeadZoneMm } from "./dragToCurveDeadZone";
import {
  RegularPolygonGesture,
  type PolygonPreviewShape,
} from "./regularPolygonGesture";
import {
  makeSlotPathData,
  SLOT_DEFAULT_LENGTH_RATIO,
  SLOT_DEFAULT_WIDTH_MM,
} from "./CutoutTool";
import { DimensionLabel } from "../canvas/DimensionLabel";
import { DrawGestureEngine } from "./drawGestureEngine";
import type { DrawGestureHost } from "./drawGestureEngine";
import {
  canonicalizeConstructionLine,
  modifierFor,
} from "../model/constructionLineNormalize";
import { computeDefaultSmoothAnchors } from "../model/constructionAnchors";
import type { ToolSettings } from "./EditorContext";
import type { ConstructionSnapController } from "./constructionSnapController";
import type { ConstructionKind } from "./ConstructionTool";
import { DEFAULT_FREEHAND_SMOOTHNESS } from "./ConstructionTool";

const PREVIEW_STROKE = () => cssVar("--construction-preview");

/** Everything the DrawSubModeController needs from its owning tool. The
 *  Draw-mode state + the drag-out / poly-commit helpers live in the
 *  controller; the shared machinery (scene / project / settings /
 *  commit / selection, the construction snap tiers, the sibling-gesture
 *  reset, and the modifier-replay dispatch back through the tool's full
 *  pointer handlers) stays on ConstructionTool and is injected through
 *  this host. */
export interface DrawSubModeHost {
  // ---- core context -------------------------------------------------
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  getSettings(): ToolSettings;
  getViewportView(): paper.View;
  commit(label: string, mutate: (proj: Project) => Project): void;
  setSelection(sel: {
    pieceId: null;
    decorationId: null;
    constructionId: string;
    constructionIds?: string[];
  }): void;

  // ---- shared construction snap machinery ---------------------------
  /** The shared `ConstructionSnapController` (#839) — the draw draft
   *  snaps through the same tiers as every other sub-mode. */
  getSnapController(): ConstructionSnapController;

  // ---- sibling-gesture teardown -------------------------------------
  /** Reset the offset / round / mirror controllers, exactly as the
   *  original `cancelDraft` did inline. Called from `cancelDraft` at the
   *  original position so a draw teardown also clears any adjacent
   *  gesture's transient state / overlays. */
  resetSiblingGestures(): void;

  // ---- modifier replay ----------------------------------------------
  /** Re-fire the tool's full `onMove` pointer handler with synthesized
   *  modifiers (#modifier-replay). Routed through the tool so the replay
   *  runs the identical dispatch chain a real move would. */
  dispatchOnMove(s: PointerSample, p: { x: number; y: number }): void;
  /** Re-fire the tool's full `onHover` pointer handler. */
  dispatchOnHover(s: PointerSample, p: { x: number; y: number }): void;
}

export class DrawSubModeController implements DrawGestureHost {
  /** Active draw kind (rect / ellipse / slot / line / freehand /
   *  polyline / polygon). Owned here; ConstructionTool exposes a `kind`
   *  getter + `setKind` / `setSlotWidth` delegators for app.ts. */
  kind: ConstructionKind = "rect";
  /** Angle modifier for POLYGON gestures: Ctrl / Cmd (or the Angle toggle)
   *  only — Shift already means "regular / equilateral" here, so it must not
   *  also lock the drag's angle (mirrors rect/ellipse, where Shift means
   *  square and never angle-snaps). */
  private polygonAngleSnap(s: PointerSample): boolean {
    return isAngleSnapActive(this.host.getSettings(), s.ctrlKey || s.metaKey);
  }

  /** The shared regular-polygon state machine (#928/#931/#934) — the auto-n
   *  two-stage flow, click-move-click, the pinned-size aim stage, and the
   *  pinned-circle variable count. Extracted so the leather Shape / Cutout /
   *  Stitch tools run their own instances of the exact same gesture. The
   *  ghost renders in construction's preview style via `renderPolygonGhost`;
   *  commits land as ordinary closed polygon-kind lines. */
  private polyGesture = new RegularPolygonGesture({
    getScope: () => this.host.getScope(),
    getScene: () => this.host.getScene(),
    getViewportView: () => this.host.getViewportView(),
    getSettings: () => this.host.getSettings(),
    renderPreview: (shape) => this.renderPolygonGhost(shape),
    clearPreview: () => this.cleanupPreview(),
    commitVertices: (pts) => {
      const pathData =
        pts.map((v, i) => `${i === 0 ? "M" : "L"}${v.x} ${v.y}`).join(" ") +
        " Z";
      // Regular rings get their well-known shape name (Triangle / Square /
      // Pentagon / Hexagon / Octagon) instead of the generic "Polygon".
      this.commitPathData(
        pathData,
        undefined,
        "polygon",
        {},
        regularPolygonModifier(pts.length) ?? undefined,
      );
    },
    notifyDraftChange: () => this.engine.notifyDraftChange(),
    liveSides: (sides, radiusMm) => this.polygonLiveCb?.(sides, radiusMm),
    liveSideLength: (mm) => this.polygonLiveSideLenCb?.(mm),
    liveRadius: (mm) => this.polygonLiveRadiusCb?.(mm),
    liveClear: () => this.polygonLiveClearCb?.(),
  });
  /** Live panel mirror for the auto gesture (#931) — injected by app.ts. */
  private polygonLiveCb: ((sides: number, radiusMm: number) => void) | null =
    null;
  private polygonLiveSideLenCb: ((mm: number) => void) | null = null;
  private polygonLiveRadiusCb: ((mm: number) => void) | null = null;
  private polygonLiveClearCb: (() => void) | null = null;

  /** Re-render an in-flight regular-polygon stage after a panel value edit
   *  (#934) — app.ts calls this on settings changes. */
  refreshPolygonGesture(): void {
    this.polyGesture.refresh();
  }

  setPolygonLiveCallbacks(
    live: (sides: number, radiusMm: number) => void,
    liveSideLength: (mm: number) => void,
    liveRadius: (mm: number) => void,
    clear: () => void,
  ): void {
    this.polygonLiveCb = live;
    this.polygonLiveSideLenCb = liveSideLength;
    this.polygonLiveRadiusCb = liveRadius;
    this.polygonLiveClearCb = clear;
  }
  /** Width (short axis) of the slot capsule when `kind === "slot"`. */
  slotWidth = SLOT_DEFAULT_WIDTH_MM;
  /** User-locked bounding-box dimensions for rect / ellipse. `null`
   *  means the dimension comes from the live cursor delta instead.
   *  When both are non-null, a click without dragging places a
   *  fixed-size shape at the click point. */
  private presetWidth: number | null = null;
  private presetHeight: number | null = null;
  /** Whether the in-flight rect / ellipse draft grows symmetrically about
   *  its start point ("draw from centre") — latched per sample from
   *  {@link isCenterSnapActive} (Alt held or the centre snap toggle) so
   *  `resolveBoundingBox` can read it without a pointer sample (issue #389). */
  private drawFromCenter = false;
  /** Fixed line length (mm) when `kind === "line"`. `null` means the
   *  line follows the cursor distance live. When set, a click without
   *  dragging places a fixed-length line from the click point. */
  private presetLineLength: number | null = null;
  /** Fixed line angle (degrees, measured visually CCW from horizontal
   *  right) when `kind === "line"`. `null` means the angle follows the
   *  cursor direction live. */
  private presetLineAngleDeg: number | null = null;
  /** Corner radius (mm) a rectangle is DRAWN with (#1069) — set by a picked
   *  standard size made with rounded corners, or typed in. Its own preset:
   *  the Round sub-tool's radius arms a click on an EXISTING corner, and
   *  reusing it would quietly round every rectangle drawn afterwards. */
  private drawCornerRadius: number | null = null;
  /** Smoothness (Paper.js `path.simplify` tolerance) seeded onto a
   *  freshly-drawn freehand line (issue #416). Mirrors the
   *  per-line "Smoothness" slider but acts as the Draw tool's
   *  default so the user can dial in their preferred curve fit
   *  before drawing. Persisted across reloads via app.ts. */
  private presetFreehandSmoothness = DEFAULT_FREEHAND_SMOOTHNESS;
  /** Live "W × H" readout shown over the rect / ellipse drag preview. */
  private dimensionLabel = new DimensionLabel(PREVIEW_STROKE());
  /** The first click of a Line drawn from its centre (#389, #1113): the
   *  midpoint the start is mirrored through. Null when no Line draft. */
  private lineCenter: paper.Point | null = null;
  /** Fired during onMove so the properties panel can mirror the
   *  in-progress drag in its width/height inputs. */
  private liveDimsCallback?: (w: number, h: number) => void;
  /** Fired during onMove for the line draw so the properties panel
   *  can mirror the in-progress length / angle in its inputs. Angle
   *  is reported in degrees measured visually CCW from horizontal
   *  right (the same convention the angle indicator chip uses). */
  private liveLineCallback?: (lengthMm: number, angleDeg: number) => void;

  // Drag-state (rect / ellipse / slot / freehand).
  private startPt: paper.Point | null = null;
  private currentPt: paper.Point | null = null;
  private buffer: { x: number; y: number }[] = [];
  private previewItem: paper.Item | null = null;
  /** Most-recent pointer sample / project-space cursor from Draw
   *  sub-mode `onMove`, captured so `onModifiersChanged` can re-
   *  fire the live preview when Shift is pressed mid-drag. Only
   *  populated during Draw — other sub-modes (Edit / Round / etc.)
   *  don't have modifier-sensitive previews, so they leave these
   *  null and the helper short-circuits. */
  private lastDrawSample: PointerSample | null = null;
  private lastDrawP: { x: number; y: number } | null = null;
  /** Raw press position of the current polyline click — the preset drag
   *  gesture (#937) compares the RELEASE against it (press ≈ release =
   *  plain click; farther = a drag that places the far vertex too). */
  private polylinePressP: { x: number; y: number } | null = null;
  /** Which reference the polyline Angle step's winning candidate used on
   *  the last constrained hover (#938) — feeds the ray's single-marker
   *  choice. null = no step decision (the engine defaults to corner). */
  private polylineAngleRef: "horizontal" | "corner" | null = null;
  /** The existing line this frame's angle snap measured from (#162), or null
   *  when the snap came off the absolute table. Read by the ray emitters so
   *  the reference is drawn as a dashed ghost arm with the angle between it
   *  and the segment. Reset at the top of every frame. */
  private angleReference: AngleReference | null = null;

  /** Angle snap for this controller's gestures, recording which existing line
   *  (if any) the winning angle was measured from (#162). Every angle-snap
   *  site in the construction draw goes through here so the reference is
   *  always in step with the direction actually taken. */
  private snapAngleFrom(
    origin: { x: number; y: number },
    cursor: { x: number; y: number },
  ): AngleSnapResult {
    const sn = this.snapController.snapAngle(origin, cursor);
    this.angleReference = sn.reference;
    return sn;
  }

  /** The ghost arm point for this frame's angle reference (#162), or null when
   *  no reference won. Sized to the segment so the arc reads at the same scale
   *  as the geometry, with a floor so a very short segment still shows one. */
  private referenceGhostFor(
    origin: { x: number; y: number },
    toward: { x: number; y: number },
  ): { x: number; y: number } | null {
    const ref = this.angleReference;
    if (!ref) return null;
    const dx = toward.x - origin.x;
    const dy = toward.y - origin.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return null;
    const ghost = referenceGhostPoint(
      origin,
      ref.dirRad,
      toward,
      Math.max(10, len),
    );
    // A segment snapped PARALLEL to its reference has no angle to mark, and
    // the overlay skips a 0° corner anyway — so asking for the arc here would
    // suppress the from-horizontal marker and leave the ray with no angle at
    // all. Fall back to the horizontal marker: parallel means the segment's
    // own direction IS the reference's, so that number is the right one to
    // read.
    const gx = ghost.x - origin.x;
    const gy = ghost.y - origin.y;
    const between = Math.abs(Math.atan2(dy, dx) - Math.atan2(gy, gx));
    if (between < (0.5 * Math.PI) / 180) return null;
    return ghost;
  }

  // Click-to-add-vertices gesture (polyline / polygon). The point buffer,
  // per-vertex bezier handles (#217), hover point, preview lifecycle, close-
  // snap, segment rays, continuation state (#328), and the fly-out/draft
  // contract all live in this composable engine (#804 IE1). This controller
  // owns it, drives it from the poly draw branches of onDown/onMove/onHover/
  // onUp, and injects every construction-specific concern (snap tiers,
  // constraint recording, continuation seeding, commit) via the
  // `DrawGestureHost` interface it implements. See
  // docs/draw-tool-architecture.md.
  private engine = new DrawGestureEngine(this);

  constructor(private host: DrawSubModeHost) {}

  /** The shared `ConstructionSnapController` (#839), reached through the
   *  host. Keeps the copied draw logic's `this.snapController.…` calls
   *  intact while the snap STATE stays owned by ConstructionTool. */
  private get snapController(): ConstructionSnapController {
    return this.host.getSnapController();
  }

  // ---- public setters (delegated from ConstructionTool) -------------

  setKind(kind: ConstructionKind) {
    this.kind = kind;
    this.cancelDraft();
  }

  setSlotWidth(width: number) {
    if (width <= 0) return;
    this.slotWidth = width;
    this.kind = "slot";
  }

  setPresetWidth(w: number | null) {
    this.presetWidth = w;
  }

  setPresetHeight(h: number | null) {
    this.presetHeight = h;
  }

  setPresetLineLength(mm: number | null) {
    this.presetLineLength = mm;
  }

  setPresetLineAngleDeg(deg: number | null) {
    this.presetLineAngleDeg = deg;
  }

  /** Set the corner radius the next rectangle is drawn with (#1069). */
  setDrawCornerRadius(mm: number | null) {
    this.drawCornerRadius = mm;
  }

  /** Set the default smoothness applied to the next freehand line
   *  (issue #416). Clamped to the same 0–10 range the per-line
   *  slider exposes. */
  setPresetFreehandSmoothness(v: number) {
    this.presetFreehandSmoothness = Math.min(10, Math.max(0, v));
  }

  setLiveDimsCallback(fn: ((w: number, h: number) => void) | undefined) {
    this.liveDimsCallback = fn;
  }

  setLiveLineCallback(
    fn: ((lengthMm: number, angleDeg: number) => void) | undefined,
  ) {
    this.liveLineCallback = fn;
  }

  // ---- fly-out / draft contract (delegated from ConstructionTool) ---

  /** Number of vertices in an in-progress polyline / polygon draft.
   *  Always 0 for drag-style kinds. */
  get vertexCount(): number {
    // Regular-polygon stages (#931/#934): surface them to the shared draft
    // fly-out — stage 2 / the aim stage report 2 (✓ enabled; commits the
    // preview), an anchored first point reports 1 (✓ disabled, ✗ discards).
    if (this.kind === "polygon") {
      const staged = this.polyGesture.vertexCount();
      if (staged > 0) return staged;
    }
    return this.engine.vertexCount;
  }

  setDraftChangeListener(fn: ((vertexCount: number) => void) | null) {
    this.engine.setDraftChangeListener(fn);
  }

  /** End a polyline / polygon at the last clicked vertex. */
  handleFinish(): boolean {
    // Regular-polygon stages (#931/#934): Enter commits the staged preview
    // (stage 2 at the last hovered cursor; the aim stage at the last aimed
    // direction); an anchored first point alone abandons like Escape.
    if (this.kind === "polygon" && this.polyGesture.finish()) return true;
    return this.engine.handleFinish();
  }

  /** Escape pressed mid-drag of a polyline bezier-handle gesture
   *  (pen-tool style). Delegated to the draw engine, which pops the
   *  in-progress vertex + handle and leaves the rest of the draft intact.
   *  Returns true if there was a drag to abort, so the caller can
   *  preventDefault on the keypress without falling through to the
   *  commit-on-Escape branch. */
  handleEscapeDuringDrag(): boolean {
    // Regular-polygon stages (#931/#934): Escape abandons the staged draft.
    if (this.kind === "polygon" && this.polyGesture.escape()) return true;
    return this.engine.handleEscapeDuringDrag();
  }

  /** Discard the current draft (any kind). Returns true if there was
   *  one to drop. */
  handleDiscard(): boolean {
    if (
      this.engine.vertexCount === 0 &&
      !this.startPt &&
      this.buffer.length === 0 &&
      !this.polyGesture.stageActive
    ) {
      return false;
    }
    this.cancelDraft();
    return true;
  }

  /** Double-click finishes a polyline / polygon draft. */
  finishOnDoubleClick(): boolean {
    return this.engine.finishOnDoubleClick();
  }

  /** Release ends the bezier-drag for the most-recently-placed
   *  polyline vertex; the handles set during the drag stay on the
   *  vertex. Dispatched from the tool's `onUp` at its original position. */
  clearBezierDrag(): void {
    this.engine.clearBezierDrag();
  }

  // ---- shared preview slot (delegated from ConstructionTool) --------
  // The Draw preview + the ResizeController's corner-resize preview
  // share this one canvas slot (#838). ConstructionTool keeps
  // `cleanupPreview` / `setPreviewItem` delegators so the ResizeHost
  // contract is unchanged; the slot physically lives here now.

  cleanupPreview(): void {
    if (this.previewItem) {
      this.previewItem.remove();
      this.previewItem = null;
    }
    this.dimensionLabel.remove();
  }

  setPreviewItem(item: paper.Item | null): void {
    this.previewItem = item;
  }

  // ---- modifier replay ----------------------------------------------

  /** Capture the latest Draw-mode pointer sample so a mid-drag Shift
   *  press can replay the preview. Called from the tool's `onMove`
   *  before the sibling-controller dispatches (original position). */
  rememberSample(s: PointerSample, p: { x: number; y: number }): void {
    this.lastDrawSample = s;
    this.lastDrawP = p;
  }

  /** Replay the latest Draw-mode pointer event with new modifier flags
   *  so an in-flight rect / ellipse / slot / line draft re-evaluates
   *  size-snap and angle-snap immediately. No-op when there's no
   *  captured cursor. Routes the replay through the tool's full pointer
   *  handlers so the identical dispatch chain runs. */
  replayModifiers(modifiers: {
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
  }): void {
    if (!this.lastDrawSample || !this.lastDrawP) return;
    const synth: PointerSample = { ...this.lastDrawSample, ...modifiers };
    // Polyline / polygon drafts read the cursor through onHover
    // when no button is down; rect / ellipse / slot / line use
    // onMove while dragging. Pick whichever fits the current
    // draft state — `startPt` is non-null only while a drag is
    // in flight.
    if (this.startPt) {
      this.host.dispatchOnMove(synth, this.lastDrawP);
    } else if (this.engine.vertexCount > 0) {
      this.host.dispatchOnHover(synth, this.lastDrawP);
    }
  }

  // ---- pointer flow -------------------------------------------------

  /** Regular-polygon drag-out mode (#928): the polygon kind switches from
   *  click-to-add to a rect-style drag when the Equilateral toggle is on OR
   *  the size / centre snap is active. Centre snap makes the press point the
   *  polygon's CENTRE (drag = circumscribed radius); otherwise the drag is
   *  the first SIDE. Never engages mid-draft — an in-progress click-to-add
   *  polygon keeps its gesture even if a modifier is pressed. */
  private regularPolygonMode(s: PointerSample): boolean {
    if (this.kind !== "polygon") return false;
    return (
      this.host.getSettings().polygonEquilateral ||
      this.snapController.shouldSnapSizes(s) ||
      this.snapController.shouldSnapCenter(s)
    );
  }

  onDown(s: PointerSample, p: { x: number; y: number }) {
    const scope = this.host.getScope();
    // Regular-polygon stages (#931/#934): the press commits the aimed /
    // previewed polygon, or ends the click-move-click first side.
    if (this.kind === "polygon" && this.polyGesture.press(s, p)) return;
    // Draw sub-mode (default) — the existing drawing flow. A single Line
    // draws exactly like a polyline since #1113 — click, click, drag at the
    // end to curve — and only differs in finishing after its one segment.
    if (
      this.isPolylineFlow() ||
      (this.kind === "polygon" &&
        !(this.engine.vertexCount === 0 && this.regularPolygonMode(s)))
    ) {
      // Click-to-add-vertices gesture, fully driven by the shared draw
      // engine (#804 IE1). Continuation seeding (#328), the construction
      // snap tiers, and the commit are injected through the DrawGestureHost
      // methods this tool implements (`seedContinuation`,
      // `resolveDraftPlacement`, `commitDraft`).
      this.polylinePressP = { x: p.x, y: p.y };
      this.engine.onDown(s, p);
      return;
    }

    // Drag-style kinds. Snap the start point to an existing anchor
    // when one's in range — the new shape's first corner lands on
    // the target and a coincident constraint pins it on commit.
    // Grid snap (#259) is the lowest-priority fallback: only fires
    // when no anchor snap caught the point, mirroring the move-
    // path priority ordering in SelectTool / groupDrag.
    const startSnap = this.snapController.findDraftSnap(p);
    const startProject = startSnap
      ? new scope.Point(startSnap.pos.x, startSnap.pos.y)
      : (() => {
          const g = this.snapController.maybeSnapToGrid(p);
          return new scope.Point(g.x, g.y);
        })();
    this.startPt = startProject;
    this.currentPt = startProject;
    this.drawFromCenter = this.snapController.shouldSnapCenter(s);
    this.snapController.recordDraftSnap(startSnap);
    if (this.kind === "freehand") {
      this.buffer = [{ x: startProject.x, y: startProject.y }];
    }
  }

  onMove(s: PointerSample, p: { x: number; y: number }) {
    // Stale from the previous frame otherwise: only `snapAngleFrom` sets it,
    // and plenty of frames never reach an angle snap at all.
    this.angleReference = null;
    if (
      this.isPolylineFlow() ||
      // Regular-polygon drag (#928) is in flight only while `startPt` is set;
      // any other polygon state stays with the click-to-add engine.
      (this.kind === "polygon" && !this.startPt)
    ) {
      // Pen-tool bezier-handle drag (#217) — engine-owned gesture flow.
      this.engine.onMove(s, p);
      return;
    }
    const scope = this.host.getScope();
    let pt: { x: number; y: number } = p;
    // Set when an angle/axis snap fixed the direction this frame — line snap
    // (#323) then lands on a line ALONG that ray instead of being skipped.
    let angleSnapped = false;
    this.drawFromCenter = this.snapController.shouldSnapCenter(s);
    // Draft anchor snap wins over the shift-axis / square-constrain
    // snap — landing exactly on an existing anchor is more specific
    // than rounding to an axis. Clearing the angle hint when an
    // anchor snap fires avoids the chip lying about the gesture.
    //
    // Exception (#140): when size-snap is active for a rect/ellipse,
    // a draft snap whose target is **not on the 1:1 diagonal** would
    // distort the square into a rectangle (or the circle into an
    // ellipse), defeating the size-snap constraint the user opted
    // into. Reject such snaps and fall through to `constrainSquare`.
    // Snaps that *happen* to land on the diagonal (within the snap
    // radius) are still honoured so anchor-snapping works on the
    // axes a square would naturally pass through.
    let draftSnap = this.snapController.findDraftSnap(p);
    if (
      draftSnap &&
      this.startPt &&
      (this.kind === "rect" || this.kind === "ellipse") &&
      this.snapController.shouldSnapSizes(s)
    ) {
      const dxs = Math.abs(draftSnap.pos.x - this.startPt.x);
      const dys = Math.abs(draftSnap.pos.y - this.startPt.y);
      if (Math.abs(dxs - dys) > this.snapController.snapRadiusMm()) {
        draftSnap = null;
      }
    }
    if (draftSnap) {
      pt = draftSnap.pos;
      setAngleHint(null);
    } else if (this.startPt) {
      if (this.kind === "slot") {
        if (this.snapController.shouldSnapAngle(s)) {
          const sn = this.snapAngleFrom(this.startPt, p);
          pt = { x: sn.x, y: sn.y };
          setAngleHint(sn.angleRad);
          angleSnapped = true;
        } else {
          setAngleHint(null);
        }
      } else if (
        (this.kind === "rect" || this.kind === "ellipse") &&
        this.snapController.shouldSnapSizes(s)
      ) {
        pt = constrainSquare(this.startPt, p);
      }
    } else if (this.kind === "slot") {
      setAngleHint(null);
    }
    // Grid snap (#259) is the lowest-priority draft fallback.
    // Apply only when no higher-priority snap moved the point off
    // the raw cursor — `pt === p` means none of the branches above
    // adjusted it. Skip for freehand (every cursor sample becomes
    // a vertex, snapping each to the grid would land them on top
    // of each other) and for size-snapped rect / ellipse (the
    // size-snap explicitly preserves square / circle aspect, which
    // grid rounding would distort).
    let lineSnapPos: { x: number; y: number } | null = null;
    const sizeSnappedBox =
      (this.kind === "rect" || this.kind === "ellipse") &&
      this.snapController.shouldSnapSizes(s);
    if (!draftSnap && this.kind !== "freehand" && !sizeSnappedBox) {
      if (pt === p) {
        // No higher-priority snap took the point — snap straight to the nearest
        // line (#323, wins over grid), else the grid. Computed once here so the
        // indicator below can mark the snapped end without re-finding it.
        // A crossing of two lines (#128) is more specific than a point along
        // one, so it wins; both ride the Line-snap toggle.
        lineSnapPos =
          this.snapController.findIntersectionSnap(p) ??
          this.snapController.findLineSnap(p);
        if (lineSnapPos) {
          pt = { x: lineSnapPos.x, y: lineSnapPos.y };
        } else if (isGridSnapActive(this.host.getSettings())) {
          const grid = this.host.getScene().getMatGridInfo();
          if (grid) {
            const g = snapPointToGrid(p, grid);
            pt = { x: g.x, y: g.y };
          }
        }
      } else if (angleSnapped && this.startPt) {
        // Angle snap fixed the direction; the END snaps onto where that ray
        // crosses the nearest line (#323), keeping the clean angle. The dot
        // sits on the line at the crossing (= the line's end).
        const end = this.snapController.angleLineEnd(this.startPt, pt);
        if (end) {
          pt = end;
          lineSnapPos = end;
        }
      }
    }
    this.snapController.updateDraftHoverSnap(draftSnap);
    // Keep the snap dot live on the dragged END too, not just on hover (#323):
    // for a straight line snap it sits on the end; under angle snap it marks
    // where the cursor latched onto the line. Anchor snaps show their own ring;
    // grid stays silent mid-drag (unchanged).
    this.host
      .getScene()
      .setConstructionDraftSnapPoint(draftSnap ? null : lineSnapPos);
    this.currentPt = new scope.Point(pt.x, pt.y);
    if (this.kind === "freehand") {
      const last = this.buffer[this.buffer.length - 1];
      if (!last || Math.hypot(pt.x - last.x, pt.y - last.y) > 1.0) {
        this.buffer.push({ x: pt.x, y: pt.y });
      }
    }
    if (
      (this.kind === "rect" || this.kind === "ellipse") &&
      this.startPt &&
      this.currentPt &&
      this.liveDimsCallback
    ) {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      this.liveDimsCallback(box.width, box.height);
    }
    // #747: while drawing a slot, draw an on-canvas ray from the start point
    // showing its length and angle (whether or not it's angle-snapped — #729).
    // Cleared for the area kinds and on draft teardown. (A Line draws through
    // the click-to-add engine since #1113, which emits its own ray.)
    if (this.kind === "slot" && this.startPt) {
      // With a line reference in play (#162) the ray shows the angle measured
      // from THAT line — a dashed ghost arm along it plus the arc between —
      // instead of the from-horizontal marker, which would be the wrong number
      // to read. `angleRef: "corner"` selects the arc; the ghost flag keeps the
      // arm dashed so it doesn't read as part of the shape being drawn.
      const ghost = this.referenceGhostFor(this.startPt, this.currentPt);
      emitSegmentRay(
        this.host.getScene(),
        this.startPt,
        this.currentPt,
        ghost ?? undefined,
        undefined,
        ghost ? "corner" : undefined,
        ghost != null,
      );
    } else {
      this.host.getScene().setGuideRays(null);
    }
    this.updateDragPreview();
  }

  onHover(s: PointerSample, p: { x: number; y: number }) {
    // Capture for `onModifiersChanged` — same reasoning as onMove.
    this.lastDrawSample = s;
    this.lastDrawP = p;
    this.angleReference = null;
    // Regular-polygon stages (#931/#934): the hover rotates the aimed
    // polygon, rubber-bands the click-move-click side, or drives the auto-n
    // stage-2 preview.
    if (this.kind === "polygon" && this.polyGesture.hover(s, p)) return;
    return this.hoverTail(s, p);
  }

  /** Ghost renderer for the shared polygon gesture — construction's preview
   *  style (the same yellow dashed stroke every draw draft uses). */
  private renderPolygonGhost(shape: PolygonPreviewShape): void {
    this.cleanupPreview();
    const scope = this.host.getScope();
    const item =
      shape.kind === "line"
        ? new scope.Path({
            segments: [
              new scope.Point(shape.a.x, shape.a.y),
              new scope.Point(shape.b.x, shape.b.y),
            ],
          })
        : new scope.Path({
            segments: shape.vertices.map((v) => new scope.Point(v.x, v.y)),
            closed: true,
          });
    item.strokeColor = new scope.Color(PREVIEW_STROKE());
    item.strokeWidth = 1.6;
    item.strokeScaling = false;
    item.dashArray = [2, 1.5];
    item.fillColor = null;
    item.data = { ephemeral: true };
    this.previewItem = item;
  }

  /** Remainder of the hover flow (engine routing + drag-kind snap rings). */
  private hoverTail(s: PointerSample, p: { x: number; y: number }): void {
    if (this.isPolylineFlow() || (this.kind === "polygon" && !this.startPt)) {
      // Rubber-band hover, engine-owned flow (#804 IE1). The construction
      // snap tiers + hover rings are injected via `onEmptyDraftHover` /
      // `resolveDraftHover`.
      this.engine.onHover(s, p);
      return;
    }
    // Drag-style shapes: hover (no button held) still gets a snap
    // ring preview so the user can see where a click would land.
    const anchorSnap = this.snapController.findDraftSnap(p);
    this.snapController.updateDraftHoverSnap(anchorSnap);
    this.snapController.updateDraftGridHover(p, !!anchorSnap);
  }

  onUp(s: PointerSample, p: { x: number; y: number }) {
    if (this.isPolylineFlow() || (this.kind === "polygon" && !this.startPt)) {
      // A pointer with no hover places its vertex on RELEASE (#1116) — the
      // press started the segment and the drag previewed it.
      this.engine.onUp(s, p);
      // Polyline presets (#937): with the bezier handle gesture suppressed
      // (Segment length / Angle step set), a press-DRAG reads as "draw the
      // segment" — the release places the far vertex through the same
      // constrained placement a click would use. The dead-zone keeps a
      // plain click (press ≈ release) from double-placing.
      // (A mouse only — a pointer with no hover already placed above.)
      if (
        this.isPolylineFlow() &&
        !this.bezierEnabled &&
        hasHover(s.pointerType) &&
        this.engine.vertexCount > 0 &&
        this.polylinePressP
      ) {
        const press = this.polylinePressP;
        this.polylinePressP = null;
        const scope = this.host.getScope();
        const deadZone = dragToCurveDeadZoneMm(
          s.pointerType,
          scope.view?.zoom ?? 1,
        );
        if (Math.hypot(p.x - press.x, p.y - press.y) > deadZone) {
          this.engine.onDown(s, p);
        }
      }
      // A single Line ends the moment its segment exists (#1113) — on this
      // release, so a drag-to-curve at the end vertex is part of it.
      this.engine.maybeFinishSingleSegment();
      return;
    }

    // Capture the end-point snap before we commit. `draftHoverSnap`
    // tracks the candidate the cursor is currently over; the
    // per-frame `onMove` already adjusted `currentPt` to land on
    // it, so its project position == the snapped vertex's
    // position. Pushing the record here closes the loop: the
    // commit's pathData matching pass turns this into the second
    // coincident constraint.
    if (this.currentPt) {
      this.snapController.commitHoverSnap({
        x: this.currentPt.x,
        y: this.currentPt.y,
      });
    }

    let pathData: string | null = null;
    let primitive: ConstructionPrimitive | undefined;
    // Captured only on the freehand branch — the raw pointer samples
    // that become the line's `rawPoints` source-of-truth for the
    // Smoothness slider (issue #171). Cleared for every other kind.
    let freehandRawPoints: { x: number; y: number }[] | undefined;
    const scope = this.host.getScope();
    if (this.kind === "polygon" && this.startPt && this.currentPt) {
      // Regular-polygon release (#928/#931/#934): the shared gesture commits
      // the fixed-count ring (as a closed polygon-kind line — same shape the
      // click-to-add gesture commits, so downstream editing behaves
      // identically), or transitions into a stage (auto-n stage 2 / the
      // click-move-click anchor / the pinned-size aim anchor).
      const a = { x: this.startPt.x, y: this.startPt.y };
      const b = { x: this.currentPt.x, y: this.currentPt.y };
      const fromCenter = this.drawFromCenter;
      this.startPt = null;
      this.currentPt = null;
      this.drawFromCenter = false;
      setAngleHint(null);
      this.polyGesture.release(a, b, fromCenter);
      return;
    } else if (this.kind === "rect" && this.startPt && this.currentPt) {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 1 && box.height > 1) {
        const right = box.left + box.width;
        const bottom = box.top + box.height;
        pathData = `M${box.left} ${box.top} L${right} ${box.top} L${right} ${bottom} L${box.left} ${bottom} Z`;
        primitive = {
          kind: "rectangle",
          x: box.left,
          y: box.top,
          width: box.width,
          height: box.height,
        };
      }
    } else if (this.kind === "ellipse" && this.startPt && this.currentPt) {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 1 && box.height > 1) {
        const tmp = new scope.Path.Ellipse(
          new scope.Rectangle(
            new scope.Point(box.left, box.top),
            new scope.Size(box.width, box.height),
          ),
        );
        pathData = tmp.pathData;
        tmp.remove();
        primitive = {
          kind: "ellipse",
          x: box.left,
          y: box.top,
          width: box.width,
          height: box.height,
        };
      }
    } else if (this.kind === "freehand" && this.buffer.length > 2) {
      // Freehand stays open — user can close it visually by ending near
      // the start point. Append `Z` only when the loop is closed.
      const first = this.buffer[0]!;
      const last = this.buffer[this.buffer.length - 1]!;
      const closed = Math.hypot(last.x - first.x, last.y - first.y) < 4;
      // Capture the raw pointer samples so the Smoothness slider in
      // the properties panel can re-fit a bezier curve through them
      // post-commit (issue #171). We also pre-compute the visible
      // `pathData` at the default smoothness so the line lands in
      // its smoothed form — the derive pass in app.ts produces the
      // same string on subsequent commits, but the first commit
      // happens *inside* the derive pipeline so we have to seed it
      // here.
      freehandRawPoints = this.buffer.map((b) => ({ x: b.x, y: b.y }));
      pathData = smoothPathFromRawPoints(
        freehandRawPoints,
        this.presetFreehandSmoothness,
        closed,
        scope,
      );
    } else if (this.kind === "slot" && this.startPt && this.currentPt) {
      pathData = this.buildSlotPath(this.startPt, this.currentPt);
      // The slot tool resolves a click-without-drag into a default
      // horizontal capsule centered on the click — capture the same
      // logic here so the stored endpoints match the rendered path.
      // A real drag honours from-centre (Alt / centre snap), where the
      // press point is the capsule's midpoint rather than one cap (#389),
      // so the primitive endpoints stay in lock-step with `buildSlotPath`.
      const w = this.slotWidth;
      const dist = this.startPt.getDistance(this.currentPt);
      const useDefault = dist < 0.5;
      const halfL = (w * SLOT_DEFAULT_LENGTH_RATIO) / 2;
      const axis = useDefault
        ? {
            a: { x: this.startPt.x - halfL, y: this.startPt.y },
            b: { x: this.startPt.x + halfL, y: this.startPt.y },
          }
        : resolveDragAxis(this.startPt, this.currentPt, this.drawFromCenter);
      primitive = {
        kind: "pill",
        x1: axis.a.x,
        y1: axis.a.y,
        x2: axis.b.x,
        y2: axis.b.y,
        width: w,
      };
    }
    this.cleanupPreview();
    this.startPt = null;
    this.currentPt = null;
    this.drawFromCenter = false;
    this.buffer = [];
    setAngleHint(null);
    if (pathData) {
      // A rectangle drawn with a corner radius (#1069) comes out rounded: the
      // same `cornerRounding` the Round sub-tool writes, with the primitive
      // kept, re-derived by the commit pipeline.
      const cornerRounding =
        primitive?.kind === "rectangle" && this.drawCornerRadius
          ? roundAllCorners(
              pathData,
              this.drawCornerRadius,
              this.host.getScope(),
            )
          : undefined;
      this.commitPathData(
        pathData,
        primitive,
        undefined,
        {
          rawPoints: freehandRawPoints,
          smoothness:
            freehandRawPoints !== undefined
              ? this.presetFreehandSmoothness
              : undefined,
        },
        undefined,
        cornerRounding,
      );
    }
  }

  /** Full draft reset: the engine's poly draft (points / handles /
   *  continuation / preview) plus this controller's own drag state, and
   *  (through the host) the sibling offset / round / mirror controllers.
   *  Public because it is part of the `DrawGestureHost` contract the
   *  engine drives (`handleFinish` abandons a sub-2-vertex draft through
   *  it). */
  cancelDraft() {
    const hadPoly = this.engine.vertexCount > 0;
    this.polyGesture.cancel();
    this.engine.reset();
    this.lineCenter = null;
    this.startPt = null;
    this.currentPt = null;
    this.drawFromCenter = false;
    this.buffer = [];
    this.lastDrawSample = null;
    this.lastDrawP = null;
    this.cleanupPreview();
    this.snapController.clearDraftSnaps();
    this.host.resetSiblingGestures();
    setAngleHint(null);
    this.host.getScene().clearGuideRays();
    if (hadPoly) this.engine.notifyDraftChange();
  }

  // ---- DrawGestureHost implementation (#804 IE1) --------------------------
  // This controller owns a `DrawGestureEngine` (`this.engine`) that runs the
  // generic click-to-add polyline / polygon flow. These hooks inject every
  // construction-specific concern the engine calls back into.

  getScope(): paper.PaperScope {
    return this.host.getScope();
  }
  getScene(): Scene {
    return this.host.getScene();
  }
  previewColor(): string {
    return PREVIEW_STROKE();
  }
  isPolygon(): boolean {
    return this.kind === "polygon";
  }
  isPolyKind(): boolean {
    return this.isPolylineFlow() || this.kind === "polygon";
  }
  /** The open click-to-add flow: a polyline, or a single Line, which draws
   *  the same way and ends after one segment (#1113). */
  private isPolylineFlow(): boolean {
    return this.kind === "polyline" || this.kind === "line";
  }
  /** DrawGestureHost: a Line is done once its one segment exists (#1113). */
  finishesAfterOneSegment(): boolean {
    return this.kind === "line";
  }
  /** The Segment length / Angle step presets in force for the open flow: the
   *  polyline's own, or the Line's Length / Angle step fields (#1113). */
  private openFlowPresets(): {
    length: number | null;
    stepDeg: number | null;
  } {
    const st = this.host.getSettings();
    const pos = (v: number | null | undefined) =>
      v != null && v > 0 ? v : null;
    return this.kind === "line"
      ? {
          length: pos(this.presetLineLength),
          stepDeg: pos(this.presetLineAngleDeg),
        }
      : {
          length: pos(st.polylineSegmentLength),
          stepDeg: pos(st.polylineAngleDeg),
        };
  }
  /** Construction supports line continuation (#328). */
  get continuationEnabled(): boolean {
    return true;
  }
  /** DrawGestureHost: the polyline Angle step, threaded into the pending
   *  segment's ray labels so step multiples annotate their math (#937). */
  pendingAngleStepDeg(): number | null {
    if (!this.isPolylineFlow()) return null;
    return this.openFlowPresets().stepDeg;
  }
  /** DrawGestureHost: which reference the Angle step's winning candidate
   *  measured from (#938) — the ray then shows exactly that one marker. */
  pendingAngleRef(): "horizontal" | "corner" | null {
    return this.isPolylineFlow() ? this.polylineAngleRef : null;
  }
  /** DrawGestureHost: the existing line this frame's angle snap measured from
   *  (#162), as an arm from the pending segment's start. */
  pendingAngleReferenceArm(
    from: paper.Point,
    to: paper.Point,
  ): { x: number; y: number } | null {
    return this.referenceGhostFor(
      { x: from.x, y: from.y },
      { x: to.x, y: to.y },
    );
  }
  /** Construction supports per-vertex bezier handles (#217) — except while
   *  a polyline preset (Segment length / Angle step) is set: pinned straight
   *  segments and pen-style handle pulls don't mix, and a press-DRAG then
   *  reads as "drawing the segment" — pulling a curve handle out of the
   *  vertex instead would leave the user without the first segment their
   *  drag appeared to draw (and without the relative angle lock built on
   *  it). */
  get bezierEnabled(): boolean {
    if (this.isPolylineFlow()) {
      const { length, stepDeg } = this.openFlowPresets();
      if (length != null || stepDeg != null) return false;
    }
    return true;
  }

  /** DrawGestureHost: nearest previously-placed vertex on the current
   *  polyline / polygon draft within snap range (for the preview's mid-point
   *  halo and the click / hover self-snap tier). Skips the last placed vertex
   *  (clicking on top of it would produce a zero-length segment `commitPoly`
   *  dedupes anyway). For polygons the start (index 0) is also skipped because
   *  the wider close-snap-radius logic owns that target; polylines include the
   *  start so the user can place a vertex on top of it. */
  selfSnapAt(pt: paper.Point): { pos: paper.Point; index: number } | null {
    if (!isPointSnapActive(this.host.getSettings())) return null;
    const n = this.engine.points.length;
    if (n < 2) return null;
    const radius = this.snapController.snapRadiusMm();
    const firstIdx = this.kind === "polygon" ? 1 : 0;
    let best: { pos: paper.Point; index: number; dist: number } | null = null;
    // [firstIdx, n-2] — polygon skips the start (close-target),
    // polyline includes it. Always skip the last point.
    for (let i = firstIdx; i < n - 1; i++) {
      const v = this.engine.points[i]!;
      const d = v.getDistance(pt);
      if (d > radius) continue;
      if (!best || d < best.dist) best = { pos: v, index: i, dist: d };
    }
    return best ? { pos: best.pos, index: best.index } : null;
  }

  /** DrawGestureHost: commit the current draft. Delegates to the existing
   *  poly-commit path (dedupe → close-conversion → constraint-snap pass →
   *  continuation replace or new line). */
  commitDraft(closed: boolean): void {
    this.commitPoly(closed);
  }

  /** DrawGestureHost: first-click continuation (#328). When the empty-draft
   *  click landed on the start/end vertex of an existing OPEN polyline / line,
   *  load its vertices/handles into the engine and remember the continuation
   *  target so `commitPoly` REPLACES that line. Returns true when it seeded. */
  seedContinuation(pt: paper.Point): boolean {
    const cont = this.findContinuablePolylineEndpoint(pt);
    if (cont && this.seedDraftFromExistingLine(cont.lineId, cont.fromStart)) {
      this.engine.continuingLineId = cont.lineId;
      this.engine.continuingFromStart = cont.fromStart;
      return true;
    }
    return false;
  }

  /** DrawGestureHost: resolve a placed-vertex position through the construction
   *  snap tiers (self-snap → draft-anchor snap → angle+line-crossing → grid),
   *  recording any draft-time anchor snap for the on-commit constraint pass.
   *  Extracted verbatim from the poly-draw `onDown` branch. */
  /** Freeform click-to-add polygon constraints (#928). With the ANGLE snap
   *  active, every interior angle repeats the draft's first turn (all angles
   *  the same) — until a first turn exists, the segment falls back to the
   *  common drafting angles. With the SIZE snap active, the pending segment's
   *  length snaps to the first side's (equilateral). Returns null when no
   *  polygon constraint applies (generic snap tiers run instead). Self / anchor
   *  snaps still win over this, so closing the polygon on its start vertex
   *  keeps working. */
  private polygonDraftConstraint(
    s: PointerSample,
    p: { x: number; y: number },
  ): { x: number; y: number } | null {
    // Polyline presets (#934 follow-up): a set Segment length pins every
    // placed segment's length, and a set Angle step snaps each segment's
    // direction to multiples of it — same semantics as the Line kind's
    // Length / Angle step fields, applied per segment.
    if (this.isPolylineFlow()) {
      const pts = this.engine.points;
      if (pts.length < 1) return null;
      // A Line applies its own Length / Angle step here, the same way (#1113).
      const { length: pinL, stepDeg } = this.openFlowPresets();
      const hasLen = pinL != null;
      const hasStep = stepDeg != null;
      this.polylineAngleRef = null;
      if (!hasLen && !hasStep) return null;
      const last = pts[pts.length - 1]!;
      let end = { x: p.x, y: p.y };
      let hinted = false;
      if (hasStep) {
        const dx = end.x - last.x;
        const dy = end.y - last.y;
        const len = Math.hypot(dx, dy);
        if (len > 1e-9) {
          // Visual degrees CCW from horizontal right (project space is
          // y-down, so dy negates) — the same convention the Line kind
          // uses for the Line kind's Angle step.
          let visualDeg = (Math.atan2(-dy, dx) * 180) / Math.PI;
          if (visualDeg < 0) visualDeg += 360;
          // Candidate directions: multiples of the step from the horizontal
          // AND corner angles with the PREVIOUS segment at multiples of the
          // step. The corner angle is measured between the two segments at
          // their shared vertex — i.e. from the previous segment's BACK-ray
          // (its direction + 180°), the same angle the corner arc labels —
          // so a 72° step on a horizontal segment offers a true 72° corner
          // (direction 108°), not just 72° of turn (direction 72°, which is
          // a 108° corner). Nearest to the cursor wins. The two sets
          // coincide when the step divides 180 (45 / 60 / 90…) and every
          // segment is step-aligned; they diverge for steps like 72 or when
          // earlier segments were drawn free.
          const candidates = [Math.round(visualDeg / stepDeg) * stepDeg];
          if (pts.length >= 2) {
            const prev = pts[pts.length - 2]!;
            const pdx = last.x - prev.x;
            const pdy = last.y - prev.y;
            if (Math.hypot(pdx, pdy) > 1e-9) {
              let backDeg =
                ((Math.atan2(-pdy, pdx) * 180) / Math.PI + 180) % 360;
              if (backDeg < 0) backDeg += 360;
              const rel = visualDeg - backDeg;
              candidates.push(backDeg + Math.round(rel / stepDeg) * stepDeg);
            }
          }
          const angDist = (a: number) => {
            const d = ((((a - visualDeg) % 360) + 540) % 360) - 180;
            return Math.abs(d);
          };
          const snappedVisual = candidates.reduce((best, c) =>
            angDist(c) < angDist(best) ? c : best,
          );
          // Report which reference won so the ray shows only that angle
          // marker (#938). A tie (the sets coincide) reads as corner — the
          // more useful number mid-polyline.
          this.polylineAngleRef =
            candidates.length >= 2 &&
            angDist(candidates[1]!) <= angDist(candidates[0]!)
              ? "corner"
              : "horizontal";
          const rad = (snappedVisual * Math.PI) / 180;
          end = {
            x: last.x + Math.cos(rad) * len,
            y: last.y - Math.sin(rad) * len,
          };
          setAngleHint(-rad);
          hinted = true;
        }
      } else if (this.snapController.shouldSnapAngle(s)) {
        // No step set — the generic angle snap still composes with a
        // pinned Segment length.
        const sn = this.snapAngleFrom(last, end);
        end = { x: sn.x, y: sn.y };
        setAngleHint(sn.angleRad);
        hinted = true;
      }
      if (hasLen) end = snapSegmentToLength(last, end, pinL);
      if (!hinted) setAngleHint(null);
      return end;
    }
    if (this.kind !== "polygon") return null;
    const pts = this.engine.points;
    if (pts.length < 1) return null;
    const pinL = this.host.getSettings().polygonSideLength;
    const sizes = this.snapController.shouldSnapSizes(s) || pinL != null;
    const angle = this.polygonAngleSnap(s);
    if (!sizes && !angle) return null;
    const last = pts[pts.length - 1]!;
    let end = { x: p.x, y: p.y };
    let hinted = false;
    if (angle) {
      const firstTurn = pts.length >= 3 ? turnAt(pts[0]!, pts[1]!, pts[2]!) : 0;
      if (Math.abs(firstTurn) > 1e-6) {
        end = snapSegmentToTurn(pts[pts.length - 2]!, last, end, firstTurn);
      } else {
        const sn = this.snapAngleFrom(last, end);
        end = { x: sn.x, y: sn.y };
        setAngleHint(sn.angleRad);
        hinted = true;
      }
    }
    if (sizes) {
      // A pinned Side length applies from the very first segment (#934);
      // otherwise the size snap repeats the first drawn side.
      const sideLen =
        pinL != null && pinL > 0
          ? pinL
          : pts.length >= 2
            ? Math.hypot(pts[1]!.x - pts[0]!.x, pts[1]!.y - pts[0]!.y)
            : null;
      if (sideLen != null) end = snapSegmentToLength(last, end, sideLen);
    }
    if (!hinted) setAngleHint(null);
    return end;
  }

  resolveDraftPlacement(
    s: PointerSample,
    p: { x: number; y: number },
  ): paper.Point {
    const scope = this.host.getScope();
    const pt = new scope.Point(p.x, p.y);
    // Self-snap to a previous vertex on the in-progress draft.
    const selfSnap = this.selfSnapAt(pt);
    let snapped: paper.Point = pt;
    let draftSnap: ReturnType<typeof this.snapController.findDraftSnap> = null;
    if (selfSnap) {
      snapped = selfSnap.pos as paper.Point;
      setAngleHint(null);
    } else {
      // Draft-time anchor snap takes priority over the angle snap
      // — landing the click on an existing anchor is a more
      // specific intent than aligning to a 30°/45° direction.
      draftSnap = this.snapController.findDraftSnap(p);
      const polyConstraint = draftSnap
        ? null
        : this.polygonDraftConstraint(s, p);
      if (draftSnap) {
        snapped = new scope.Point(draftSnap.pos.x, draftSnap.pos.y);
        setAngleHint(null);
      } else if (polyConstraint) {
        // Polygon equal-angle / equal-side constraint (#928) replaces the
        // generic common-angle snap once it applies.
        snapped = new scope.Point(polyConstraint.x, polyConstraint.y);
      } else if (
        this.snapController.shouldSnapAngle(s) &&
        this.engine.points.length > 0
      ) {
        const last = this.engine.points[this.engine.points.length - 1]!;
        const sn = this.snapAngleFrom(last, p);
        snapped = new scope.Point(sn.x, sn.y);
        setAngleHint(sn.angleRad);
        // Compose with line snap (#323): snap the end onto the angle ray's
        // crossing with the nearest line.
        const end = this.snapController.angleLineEnd(last, {
          x: sn.x,
          y: sn.y,
        });
        if (end) snapped = new scope.Point(end.x, end.y);
      } else {
        // Grid snap fallback (#259): no anchor / self / angle snap
        // — round the new vertex onto the cutting-mat grid when
        // the user has Grid snap on.
        const g = this.snapController.maybeSnapToGrid(p);
        if (g.x !== p.x || g.y !== p.y) {
          snapped = new scope.Point(g.x, g.y);
        }
      }
    }
    this.snapController.recordDraftSnap(draftSnap);
    this.applyLineFromCenter(s, snapped);
    return snapped;
  }

  /** A Line drawn from its centre (Alt, or the Centre snap toggle — #389):
   *  the first click is the MIDPOINT, and the line extends symmetrically
   *  through it to the cursor. The click-to-add flow (#1113) has no separate
   *  centre, so the first vertex is re-placed as the cursor's mirror image
   *  through it on every hover and on the placing click. The preview, the
   *  length ray and the commit all read the engine's points, so they agree.
   *  Only a fresh line — a continuation starts on an existing end. */
  private applyLineFromCenter(s: PointerSample, end: paper.Point): void {
    if (this.kind !== "line" || this.engine.continuingLineId) return;
    const pts = this.engine.points;
    if (pts.length === 0) {
      this.lineCenter = null;
      return;
    }
    if (!this.lineCenter) this.lineCenter = pts[0]!.clone();
    const c = this.lineCenter;
    pts[0] = this.snapController.shouldSnapCenter(s)
      ? new (this.host.getScope().Point)(2 * c.x - end.x, 2 * c.y - end.y)
      : c.clone();
  }

  /** DrawGestureHost: resolve the rubber-band hover position through the same
   *  snap tiers, updating the anchor-hover ring, grid dot, and angle-hint chip.
   *  Extracted verbatim from the poly-draw `onHover` branch (≥1 vertex). */
  resolveDraftHover(
    s: PointerSample,
    p: { x: number; y: number },
  ): paper.Point {
    const scope = this.host.getScope();
    let pt = new scope.Point(p.x, p.y);
    // Self-snap wins over external snap, over angle snap, over grid snap.
    const selfSnap = this.selfSnapAt(pt);
    const draftSnap = selfSnap ? null : this.snapController.findDraftSnap(p);
    let gridSnapped = false;
    let angleLatch: { x: number; y: number } | null = null;
    const hoverPolyConstraint =
      selfSnap || draftSnap ? null : this.polygonDraftConstraint(s, p);
    if (selfSnap) {
      pt = selfSnap.pos;
      setAngleHint(null);
    } else if (draftSnap) {
      pt = new scope.Point(draftSnap.pos.x, draftSnap.pos.y);
      setAngleHint(null);
    } else if (hoverPolyConstraint) {
      // Polygon equal-angle / equal-side constraint (#928).
      pt = new scope.Point(hoverPolyConstraint.x, hoverPolyConstraint.y);
    } else if (this.snapController.shouldSnapAngle(s)) {
      const last = this.engine.points[this.engine.points.length - 1]!;
      const sn = this.snapAngleFrom(last, p);
      pt = new scope.Point(sn.x, sn.y);
      setAngleHint(sn.angleRad);
      // Compose with line snap (#323): snap the end onto where the angle ray
      // crosses the nearest line; the dot sits there, on the line.
      const end = this.snapController.angleLineEnd(last, { x: sn.x, y: sn.y });
      if (end) {
        pt = new scope.Point(end.x, end.y);
        angleLatch = end;
      }
    } else {
      setAngleHint(null);
      const snapTarget = this.snapController.positionalSnapTarget(p);
      if (snapTarget) {
        pt = new scope.Point(snapTarget.x, snapTarget.y);
        gridSnapped = true;
      }
    }
    this.snapController.updateDraftHoverSnap(draftSnap);
    // Grid hover dot follows the same precedence: only when no
    // higher-priority snap took the point. Self-snap / angle
    // snap have their own visuals; they suppress the grid dot too — except an
    // angle+line latch, which shows its dot on the latched line.
    if (angleLatch) {
      this.host.getScene().setConstructionDraftSnapPoint(angleLatch);
    } else {
      this.snapController.updateDraftGridHover(p, !gridSnapped);
    }
    this.applyLineFromCenter(s, pt);
    // A Line's panel mirrors the pending segment's Length / Angle live, as it
    // did while the old drag was in flight (#1113).
    if (this.kind === "line" && this.liveLineCallback) {
      const last = this.engine.points[this.engine.points.length - 1]!;
      const ldx = pt.x - last.x;
      const ldy = pt.y - last.y;
      // Visual degrees CCW from horizontal right (y-up); project space is
      // y-down, so dy negates.
      let deg = (Math.atan2(-ldy, ldx) * 180) / Math.PI;
      if (deg < 0) deg += 360;
      this.liveLineCallback(Math.hypot(ldx, ldy), deg);
    }
    return pt;
  }

  /** DrawGestureHost: `onHover` while the draft is still empty — show the
   *  first-click anchor / grid snap ring and clear any leftover ray. */
  onEmptyDraftHover(_s: PointerSample, p: { x: number; y: number }): void {
    // No vertices yet — still update the draft hover ring so the user can see
    // an upcoming first-click snap target. Grid snap hover comes second so the
    // anchor ring wins on overlap (an existing anchor is a stronger intent
    // than a grid intersection).
    const anchorSnap = this.snapController.findDraftSnap(p);
    this.snapController.updateDraftHoverSnap(anchorSnap);
    this.snapController.updateDraftGridHover(p, !!anchorSnap);
    this.host.getScene().setGuideRays(null);
  }

  // ---- drag-out preview + geometry helpers --------------------------

  /** Compute the bounding box for a rect / ellipse drag, blending the
   *  cursor delta with any preset width / height. Both presets null →
   *  free drag (Rectangle normalises in any direction). When either
   *  preset is set, the top-left anchors at the click point and only
   *  the unlocked axis follows the cursor — clicking without dragging
   *  with both presets set places a fixed-size shape. */
  private resolveBoundingBox(
    start: paper.Point,
    current: paper.Point,
  ): { left: number; top: number; width: number; height: number } {
    return resolveDragBox(
      { x: start.x, y: start.y },
      { x: current.x, y: current.y },
      {
        presetWidth: this.presetWidth,
        presetHeight: this.presetHeight,
        fromCenter: this.drawFromCenter,
      },
    );
  }

  /** Compute the auto-generated line name we'd give this primitive
   *  on commit, so the live dimension overlay can show the same
   *  label the layers panel will end up with. Mirrors
   *  `commitPathData`'s naming logic exactly (modifier from the
   *  primitive, then `nextConstructionName`). */
  private previewLineName(primitive: ConstructionPrimitive): string {
    return nextConstructionName(this.host.getProject(), modifierFor(primitive));
  }

  private updateDragPreview() {
    const scope = this.host.getScope();
    this.cleanupPreview();
    if (!this.startPt || !this.currentPt) return;
    let item: paper.PathItem | null = null;
    if (this.kind === "polygon") {
      // Regular-polygon drag feedback (#928/#931/#934): the shared gesture
      // renders the ghost (via `renderPolygonGhost`), the first-side angle
      // ray, and the live panel mirrors.
      this.polyGesture.dragFeedback(
        this.lastDrawSample,
        { x: this.startPt.x, y: this.startPt.y },
        { x: this.currentPt.x, y: this.currentPt.y },
        this.drawFromCenter,
      );
    } else if (this.kind === "rect") {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 0 && box.height > 0) {
        // Preview the draw-time corner radius the commit applies (#1069),
        // under the same rules as `roundAllCorners`: nothing below 0.5 mm,
        // and capped at half the shorter side (Paper clamps it there too).
        const rect = new scope.Rectangle(
          new scope.Point(box.left, box.top),
          new scope.Size(box.width, box.height),
        );
        const r =
          this.drawCornerRadius != null && this.drawCornerRadius >= 0.5
            ? this.drawCornerRadius
            : null;
        item = r
          ? new scope.Path.Rectangle(rect, new scope.Size(r, r))
          : new scope.Path.Rectangle(rect);
        this.dimensionLabel.update(
          scope,
          this.host.getViewportView(),
          box,
          // Preview the auto-generated name so the user sees what
          // the layers panel will call this line on commit. The
          // modifier flips between Rectangle / Square based on
          // current axis equality, exactly as commitPathData would.
          this.previewLineName({
            kind: "rectangle",
            x: box.left,
            y: box.top,
            width: box.width,
            height: box.height,
          }),
        );
      }
    } else if (this.kind === "ellipse") {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 0 && box.height > 0) {
        item = new scope.Path.Ellipse(
          new scope.Rectangle(
            new scope.Point(box.left, box.top),
            new scope.Size(box.width, box.height),
          ),
        );
        this.dimensionLabel.update(
          scope,
          this.host.getViewportView(),
          box,
          this.previewLineName({
            kind: "ellipse",
            x: box.left,
            y: box.top,
            width: box.width,
            height: box.height,
          }),
        );
      }
    } else if (this.kind === "slot") {
      const data = this.buildSlotPath(this.startPt, this.currentPt);
      if (data) item = new scope.CompoundPath({ pathData: data });
    } else if (this.kind === "freehand" && this.buffer.length > 1) {
      item = new scope.Path({
        segments: this.buffer.map((b) => new scope.Point(b.x, b.y)),
      });
    }
    if (item) {
      item.strokeColor = new scope.Color(PREVIEW_STROKE());
      item.strokeWidth = 1.6;
      item.strokeScaling = false;
      item.dashArray = [2, 1.5];
      item.fillColor = null;
      item.data = { ephemeral: true };
      this.previewItem = item;
    }
  }

  private buildSlotPath(start: paper.Point, end: paper.Point): string | null {
    const w = this.slotWidth;
    if (start.getDistance(end) < 0.5) {
      const halfL = (w * SLOT_DEFAULT_LENGTH_RATIO) / 2;
      return makeSlotPathData(
        { x: start.x - halfL, y: start.y },
        { x: start.x + halfL, y: start.y },
        w,
      );
    }
    // From-centre (Alt / centre snap) → press point is the midpoint (#389).
    const { a, b } = resolveDragAxis(start, end, this.drawFromCenter);
    return makeSlotPathData(a, b, w);
  }

  // ---- continuation + poly commit helpers ---------------------------

  /** Issue #328 — locate the OPEN polyline (or single-segment "line"
   *  kind, which is structurally a 2-vertex polyline) whose start or
   *  end vertex sits inside the project-mm snap radius of the click
   *  point. Returns the closer endpoint when both are in range, or
   *  null when no candidate matches. Closed polygons are excluded —
   *  there's no endpoint to continue from. Derived (offset / mirror)
   *  lines are excluded too: their geometry is owned by another line
   *  and can't be reshaped by appending vertices. */
  private findContinuablePolylineEndpoint(
    p: paper.Point,
  ): { lineId: string; fromStart: boolean; point: paper.Point } | null {
    const scope = this.host.getScope();
    const radius = this.snapController.snapRadiusMm();
    const lines = projectConstructionLines(this.host.getProject());
    let best: {
      lineId: string;
      fromStart: boolean;
      point: paper.Point;
      dist: number;
    } | null = null;
    for (const line of lines) {
      if (isOffsetDerived(line) || isMirrorDerived(line)) continue;
      if (!isVisible(line)) continue;
      // Compound paths parse cleanly for both `M…L…` polylines and
      // the single-`L` line primitive; close-flag and first/last
      // segment readouts work the same regardless of subpath
      // count. Open paths only — polygons close back to start, so
      // there's no endpoint to anchor a continuation.
      let cp: paper.CompoundPath | null = null;
      try {
        cp = new scope.CompoundPath({
          pathData: line.pathData,
          insert: false,
        });
      } catch {
        continue;
      }
      const sub =
        (cp.firstChild as paper.Path | undefined) ??
        (cp as unknown as paper.Path);
      if (!sub || !sub.segments || sub.segments.length < 2) {
        cp.remove();
        continue;
      }
      if (sub.closed) {
        cp.remove();
        continue;
      }
      const start = sub.segments[0]!.point;
      const end = sub.segments[sub.segments.length - 1]!.point;
      const dStart = start.getDistance(p);
      const dEnd = end.getDistance(p);
      const candidate =
        dStart <= dEnd
          ? { fromStart: true, point: start.clone(), dist: dStart }
          : { fromStart: false, point: end.clone(), dist: dEnd };
      cp.remove();
      if (candidate.dist > radius) continue;
      if (!best || candidate.dist < best.dist) {
        best = { lineId: line.id, ...candidate };
      }
    }
    if (!best) return null;
    return {
      lineId: best.lineId,
      fromStart: best.fromStart,
      point: best.point,
    };
  }

  /** Seed `polyPoints` / `polyHandles` from an existing OPEN
   *  polyline / line so a new polyline draft continues from its
   *  endpoint instead of starting fresh (issue #328). Reverses the
   *  loaded vertices when the user grabbed the line's START so the
   *  draft direction always flows "from now-the-last-vertex toward
   *  whatever the user clicks next."
   *
   *  Per-vertex bezier handles carry over — paper's Segment.handleIn
   *  / handleOut are already in the relative-to-anchor convention
   *  `polyHandles` expects. After the reverse, the in/out sides
   *  swap so the curve geometry stays identical. */
  private seedDraftFromExistingLine(
    lineId: string,
    fromStart: boolean,
  ): boolean {
    const line = projectConstructionLines(this.host.getProject()).find(
      (l) => l.id === lineId,
    );
    if (!line) return false;
    const scope = this.host.getScope();
    let cp: paper.CompoundPath;
    try {
      cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
    } catch {
      return false;
    }
    const sub =
      (cp.firstChild as paper.Path | undefined) ??
      (cp as unknown as paper.Path);
    if (!sub || !sub.segments || sub.segments.length < 2 || sub.closed) {
      cp.remove();
      return false;
    }
    const segs = sub.segments;
    const points: paper.Point[] = [];
    const handles: Array<{
      handleIn?: paper.Point;
      handleOut?: paper.Point;
    }> = [];
    for (const seg of segs) {
      points.push(seg.point.clone());
      const inH = seg.handleIn.isZero() ? undefined : seg.handleIn.clone();
      const outH = seg.handleOut.isZero() ? undefined : seg.handleOut.clone();
      handles.push({ handleIn: inH, handleOut: outH });
    }
    cp.remove();
    if (fromStart) {
      points.reverse();
      handles.reverse();
      // Reversing the vertex order requires swapping each anchor's
      // in/out tangents so the curve geometry doesn't flip — the
      // "in" handle of vertex N (now reading the other direction)
      // is the old "out" handle, and vice versa.
      for (const h of handles) {
        const tmp = h.handleIn;
        h.handleIn = h.handleOut;
        h.handleOut = tmp;
      }
    }
    this.engine.points = points;
    this.engine.handles = handles;
    return true;
  }

  private commitPoly(closed: boolean) {
    // Drop consecutive coincident points — dblclick fires two click
    // events at the same location, so a polyline finished by
    // dblclick would otherwise carry a duplicate trailing vertex
    // (a zero-length segment) and skip the 2-anchor canonicalization
    // that promotes a single-segment polyline to a Line primitive.
    // The bezier-handle slot (#217) follows the same dedupe so
    // handle indices stay aligned with the surviving vertices.
    const points: paper.Point[] = [];
    const handles: Array<{ handleIn?: paper.Point; handleOut?: paper.Point }> =
      [];
    for (let i = 0; i < this.engine.points.length; i++) {
      const p = this.engine.points[i]!;
      const prev = points[points.length - 1];
      if (!prev || prev.getDistance(p) > 0.001) {
        points.push(p);
        handles.push(this.engine.handles[i] ?? {});
      }
    }
    // Polyline finishing on the start point converts to a closed
    // polygon: after the consecutive-dedupe above, if the LAST
    // surviving point coincides with the first, drop it and mark
    // the path closed. Mirrors "user drew their way back to the
    // start; treat it as a polygon". Polygon kind already enters
    // here with `closed === true` via its own close-on-start path.
    if (
      !closed &&
      points.length >= 3 &&
      points[0]!.getDistance(points[points.length - 1]!) < 0.001
    ) {
      points.pop();
      handles.pop();
      closed = true;
    }
    // Preserve `draftSnaps` across `cancelDraft` so `commitPathData`
    // can match them to the new line's anchors and append a coincident
    // constraint per snap. `cancelDraft` unconditionally wipes draft
    // snaps to reset state when the user backs out of a draft; for a
    // polyline / polygon commit we explicitly want those snaps carried
    // into the commit reducer (issue #152).
    const preservedSnaps = this.snapController.getDraftSnaps();
    // Save the continuation target before `cancelDraft` clears it.
    // The replace-vs-create branch below reads this to decide
    // whether to swap an existing line's pathData (issue #328) or
    // append a brand-new ConstructionLine.
    const continuingId = this.engine.continuingLineId;
    // A continuation seeded from the line's START runs reversed while drafting
    // (so it flows away from the grabbed end). Put it back the right way round
    // on commit — the new vertices go in FRONT and the line keeps its
    // direction, as the Line kind's own prepend did before it joined this
    // flow (#1113).
    if (continuingId && this.engine.continuingFromStart) {
      points.reverse();
      handles.reverse();
      for (let i = 0; i < handles.length; i++) {
        const h = handles[i]!;
        handles[i] = { handleIn: h.handleOut, handleOut: h.handleIn };
      }
    }
    this.cancelDraft();
    if (points.length < 2) return;
    this.snapController.setDraftSnaps(preservedSnaps);
    const hasHandles = handles.some((h) => h.handleIn || h.handleOut);
    let data: string;
    if (hasHandles) {
      // Build the path through paper.js so bezier handles emit as
      // proper `C` segments. Paper's emit is relative-token /
      // lowercase-z, which downstream Paper-based parsing handles
      // identically — only the raw string format differs from the
      // hand-built `L` chain below.
      const scope = this.host.getScope();
      const path = new scope.Path({ insert: false });
      for (let i = 0; i < points.length; i++) {
        const p = points[i]!;
        const h = handles[i] ?? {};
        const seg = new scope.Segment(p, h.handleIn, h.handleOut);
        path.add(seg);
      }
      if (closed) path.closed = true;
      data = path.pathData;
      path.remove();
    } else {
      // No handles → preserve the historic absolute-M / absolute-L
      // / uppercase-Z format so downstream string-based tests and
      // any external consumer that scans pathData literally keep
      // working (the format predates #217 and several specs match
      // against it).
      data = `M${points[0]!.x} ${points[0]!.y}`;
      for (let i = 1; i < points.length; i++) {
        data += ` L${points[i]!.x} ${points[i]!.y}`;
      }
      if (closed) data += " Z";
    }
    if (continuingId) {
      this.commitContinuation(
        continuingId,
        data,
        closed ? "polygon" : "polyline",
      );
      return;
    }
    this.commitPathData(data, undefined, closed ? "polygon" : "polyline");
  }

  /** Issue #328 — replace an existing polyline's pathData with the
   *  user's continuation. The id, name, snapAnchors, and rotation
   *  carry over; the primitive metadata is dropped (the geometry
   *  shape no longer matches whatever parametric shape it might have
   *  had); the kind is recomputed downstream by
   *  `deriveConstructionLineKinds` to reflect the open / closed
   *  form. */
  private commitContinuation(
    lineId: string,
    pathData: string,
    kindHint: "polyline" | "polygon",
  ): void {
    this.host.commit("Continue construction line", (proj) => ({
      ...proj,
      constructionLines: (proj.constructionLines ?? []).map((l) => {
        if (l.id !== lineId) return l;
        const next: ConstructionLine = {
          ...l,
          pathData,
          kind: kindHint,
        };
        // Drop primitive metadata — the underlying shape no longer
        // matches the parametric form (a rectangle that grew a
        // fifth side isn't a rectangle anymore). The model's
        // `dropConstructionPrimitive` helper does the same when
        // the user reshapes a primitive via vertex drag etc.
        if (next.primitive) delete next.primitive;
        // Bezier-memory entries are indexed by segmentIndex, and
        // the new tail vertices have higher indices than the
        // originals — existing entries still apply. The new
        // vertices simply don't have memory yet. Safe to leave.
        return next;
      }),
    }));
    // Land the selection on the just-extended line so the
    // Properties panel pops back up on it.
    this.host.setSelection({
      pieceId: null,
      decorationId: null,
      constructionId: lineId,
      constructionIds: [lineId],
    });
  }

  private commitPathData(
    pathData: string,
    primitive?: ConstructionPrimitive,
    kindOverride?: "polyline" | "polygon" | "freehand",
    freehand?: {
      rawPoints?: { x: number; y: number }[];
      smoothness?: number;
    },
    /** Naming-modifier override — e.g. "Hexagon" for a regular 6-gon
     *  instead of the kind's generic "Polygon" bucket. */
    nameModifier?: string,
    /** Corners the line is drawn rounded with (#1069). */
    cornerRounding?: ConstructionLine["cornerRounding"],
  ) {
    const project = this.host.getProject();
    // Inferred kind: caller can pass an override (polyline / polygon
    // are drawn via `commitPoly`); otherwise we read it from the
    // active draw kind so freehand commits naturally land as
    // `kind: "freehand"`. Parametric primitives leave kind unset
    // since the primitive itself is the source of truth.
    const inferredKind: ConstructionLine["kind"] | undefined = primitive
      ? undefined
      : (kindOverride ?? (this.kind === "freehand" ? "freehand" : undefined));
    // The naming modifier is what distinguishes "Construction -
    // Square 1" from "Construction - Rectangle 1": rect / ellipse
    // become Square / Circle when their two axes are equal.
    // Polylines / polygons / freehand each get their own counter via
    // the matching capitalised modifier.
    const modifier = nameModifier
      ? nameModifier
      : primitive
        ? modifierFor(primitive)
        : inferredKind === "polyline"
          ? "Polyline"
          : inferredKind === "polygon"
            ? "Polygon"
            : inferredKind === "freehand"
              ? "Curve"
              : undefined;
    // Auto-lock smooth on every anchor that already has bezier
    // handles (ellipse / freehand / slot's arc segments). Straight
    // corners (rect / polygon / polyline) get no entry, so future
    // edits don't accidentally round them off — the user can still
    // opt in via the smooth-toggle sub-tool. Smooth-by-default
    // makes curve-bend drags propagate cleanly into adjacent
    // segments instead of breaking continuity at every corner.
    const smoothAnchors = computeDefaultSmoothAnchors(
      pathData,
      this.host.getScope(),
    );
    const line: ConstructionLine = {
      id: newId("con"),
      name: nextConstructionName(project, modifier),
      pathData,
      primitive,
      kind: inferredKind,
      smoothAnchors,
      ...(freehand?.rawPoints
        ? {
            rawPoints: freehand.rawPoints,
            smoothness: freehand.smoothness,
          }
        : {}),
      ...(cornerRounding ? { cornerRounding } : {}),
    };
    // Capture draft-time snap records before the reducer runs and
    // clear them on the tool side. Inside the reducer we'll match
    // each snap's position back to the new line's segments and
    // append a coincident constraint pinning the pair — turning
    // the visual snap into a persistent relationship.
    const snaps = isPointConstraintActive(this.host.getSettings())
      ? this.snapController.getDraftSnaps()
      : [];
    this.snapController.clearDraftSnaps();
    const scope = this.host.getScope();
    this.host.commit("Add construction line", (proj) => {
      // A 2-point polyline draw lands as a Line primitive; a
      // similar draw with bezier handles attached becomes a Curve.
      // `canonicalizeConstructionLine` does that promotion (and the
      // matching auto-rename) before the line goes into the project.
      const canonical = canonicalizeConstructionLine(line, scope, proj);
      const next = withConstructionLine(proj, canonical);
      if (snaps.length === 0) return next;
      // Parse the new line's segments once, then for each snap
      // record look up the segment whose position matches —
      // skipping snaps that don't land on any vertex (e.g., an
      // ellipse bounding-box corner has no anchor on the ellipse
      // path itself).
      const newLine = projectConstructionLines(next).find(
        (l) => l.id === line.id,
      );
      if (!newLine) return next;
      const anchors: Array<{
        pathIndex: number;
        segmentIndex: number;
        pos: { x: number; y: number };
      }> = [];
      let cp: paper.CompoundPath | null = null;
      try {
        cp = new scope.CompoundPath({
          pathData: newLine.pathData,
          insert: false,
        });
      } catch {
        return next;
      }
      const subs: paper.Path[] =
        cp.children && cp.children.length > 0
          ? (cp.children as paper.Path[])
          : [cp as unknown as paper.Path];
      subs.forEach((sub, pathIndex) => {
        sub.segments.forEach((seg, segmentIndex) => {
          anchors.push({
            pathIndex,
            segmentIndex,
            pos: { x: seg.point.x, y: seg.point.y },
          });
        });
      });
      cp.remove();
      const EPS = 0.01;
      const newConstraints: Constraint[] = [];
      const usedAnchors = new Set<string>();
      for (const snap of snaps) {
        // Segment-midpoints (issue #377) are position-only — the draft
        // already snapped onto the midpoint, but there's no constraint
        // ref to pin, so don't seed one.
        if (!isPinnableSnapRef(snap.candidate)) continue;
        const match = anchors.find(
          (a) =>
            !usedAnchors.has(`${a.pathIndex}:${a.segmentIndex}`) &&
            Math.hypot(a.pos.x - snap.pos.x, a.pos.y - snap.pos.y) < EPS,
        );
        if (!match) continue;
        usedAnchors.add(`${match.pathIndex}:${match.segmentIndex}`);
        newConstraints.push({
          id: newId(),
          kind: "coincident",
          refs: [
            {
              kind: "anchor",
              constructionId: line.id,
              pathIndex: match.pathIndex,
              segmentIndex: match.segmentIndex,
            },
            // Preserve the candidate's kind — anchor / centre /
            // bbox-edge midpoint — so a draft snap onto an
            // existing primitive's centre or midpoint commits as
            // the matching ConstraintRef instead of a fake anchor.
            snapRefToConstraintRef(snap.candidate),
          ],
        });
      }
      if (newConstraints.length === 0) return next;
      return {
        ...next,
        constraints: [...(next.constraints ?? []), ...newConstraints],
      };
    });
    this.host.setSelection({
      pieceId: null,
      decorationId: null,
      constructionId: line.id,
    });
  }
}
